import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  HostListener,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent,themeAlpine } from 'ag-grid-community';
import * as echarts from 'echarts';
import {
  VisualizationChart,
  VisualizationColumn,
  VisualizationPayload,
} from '../../services/chatbot.service';

@Component({
  selector: 'app-chatbot-visualization',
  standalone: false,
  templateUrl: './chatbot-visualization.component.html',
  styleUrl: './chatbot-visualization.component.scss',
})
export class ChatbotVisualizationComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() visualization: VisualizationPayload | undefined;

  @ViewChildren('chartHost') private chartHosts!: QueryList<ElementRef<HTMLElement>>;
  private chartInstances = new Map<HTMLElement, echarts.ECharts>();

  columnDefs: ColDef[] = [];
  rowData: Record<string, unknown>[] = [];
  readonly theme = themeAlpine.withParams({ spacing: 6, fontSize: 12 });
  private gridApi: any = null;

  private resizeObservers = new Map<HTMLElement, ResizeObserver>();
  fullscreenChartIndex: number | null = null;

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 80,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  };

   ngOnChanges(changes: SimpleChanges): void {
    if (changes['visualization']) {
      const viz = this.visualization;
      console.log('[VisualizationComponent] payload received:', {
        enabled: viz?.enabled,
        intent: viz?.intent,
        importance: viz?.importance,
        hasReport: !!viz?.report,
        rowsLength: viz?.report?.rows?.length ?? 0,
        columnsLength: viz?.report?.columns?.length ?? 0,
        chartsCount: viz?.charts?.length ?? 0,
        rowCount: viz?.metadata?.rowCounts?.['sql_result'] ?? 0,
      });
      this.syncReportGrid();
      queueMicrotask(() => this.renderCharts());
    }
  }

  ngAfterViewInit(): void {
    this.chartHosts.changes.subscribe(() => this.renderCharts());
    this.renderCharts();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    for (const instance of this.chartInstances.values()) {
      instance.resize();
    }
  }

  ngOnDestroy(): void {
    this.purgeCharts();
  }

  get charts(): VisualizationChart[] {
    return this.visualization?.charts ?? [];
  }

  hasReport(): boolean {
    const report = this.visualization?.report;
    // Only require rows to have data — columns can be auto-generated from row keys
    return !!report && Array.isArray(report.rows) && report.rows.length > 0;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    // Auto-size columns to their content/header width after grid initialises
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.autoSizeAllColumns();
      }
    }, 0);
  }

  hasVisualContent(): boolean {
    // Smart detection: Show visualization if there's actual data, even if backend didn't explicitly enable it
    const hasCharts = this.charts.length > 0;
    const hasTableData = this.hasReport();
    
    // If backend explicitly disabled, respect that
    if (this.visualization && this.visualization.enabled === false) {
      return false;
    }
    
    // If we have charts or table data, show the visualization section
    // This handles cases where backend sends data but forgets to set enabled=true
    if (hasCharts || hasTableData) {
      return true;
    }
    
    // Otherwise, respect the backend's enabled flag
    return this.visualization?.enabled === true;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private syncReportGrid(): void {
    const report = this.visualization?.report;
    if (!report || !Array.isArray(report.rows) || report.rows.length === 0) {
      this.columnDefs = [];
      this.rowData = [];
      return;
    }

    // Use backend-provided columns when available and non-empty
    const backendCols: VisualizationColumn[] = Array.isArray(report.columns) && report.columns.length > 0
      ? report.columns
      : [];

    if (backendCols.length > 0) {
      this.columnDefs = backendCols.map((col: VisualizationColumn) => ({
        field: col.field,
        headerName: col.headerName || this.toHeader(col.field),
        // Avoid 'numericColumn' type which may not be registered — handle via cellStyle instead
        cellStyle: col.type === 'number' ? { textAlign: 'right' } : undefined,
      }));
    } else {
      // Auto-generate columns from the first row's keys
      const firstRow = report.rows[0] as Record<string, unknown>;
      this.columnDefs = Object.keys(firstRow).map((key) => ({
        field: key,
        headerName: this.toHeader(key),
        cellStyle: typeof firstRow[key] === 'number' ? { textAlign: 'right' } : undefined,
      }));
    }

    this.rowData = report.rows;

    // Refresh column sizing when data changes
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 50);
  }

  get rowCount(): number {
    return this.visualization?.metadata?.rowCounts?.['sql_result'] ?? 0;
  }

  
  private renderCharts(): void {
    if (!this.chartHosts) {
      return;
    }

    const charts = this.charts;
    const hosts = this.chartHosts.toArray();

    for (let i = 0; i < hosts.length; i += 1) {
      const chart = charts[i];
      const host = hosts[i]?.nativeElement;

      if (!chart || !host || !chart.echarts?.option) {
        continue;
      }

      let instance = this.chartInstances.get(host);

      if (!instance) {
        instance = echarts.init(host);
        this.chartInstances.set(host, instance);
        this.attachResizeObserver(host, instance); // ✅ fixes shrinking
      }

      // ✅ Enhance chart config to fix axis + add zoom + clean layout
      const enhancedOption = {
        ...chart.echarts.option,

        grid: {
          containLabel: true,
          left: 50,
          right: 20,
          top: 40,
          bottom: 50,
          ...(chart.echarts.option?.['grid'] || {})
        },

        tooltip: {
          trigger: 'axis'
        },

        dataZoom: [
          {
            type: 'inside',
            zoomOnMouseWheel: true,
            moveOnMouseMove: true
          },
          {
            type: 'slider',
            height: 20
          }
        ],

        legend: {
          type: 'scroll',
          bottom: 0
        }
      };

      instance.setOption(enhancedOption, true);
      instance.resize();
    }
  }

  private attachResizeObserver(host: HTMLElement, instance: echarts.ECharts) {
    if (this.resizeObservers.has(host)) return;

    const observer = new ResizeObserver(() => {
      instance.resize();
    });

    observer.observe(host);
    this.resizeObservers.set(host, observer);
  }


  private purgeCharts(): void {
    if (!this.chartHosts) {
      return;
    }

    for (const host of this.chartHosts.toArray()) {
      const element = host.nativeElement;

      const instance = this.chartInstances.get(element);
      if (instance) {
        instance.dispose();
        this.chartInstances.delete(element);
      }

      // NEW: clean up resize observer
      const observer = this.resizeObservers.get(element);
      if (observer) {
        observer.disconnect();
        this.resizeObservers.delete(element);
      }
    }
  }

  
  toggleFullScreen(index: number) {
    this.fullscreenChartIndex = this.fullscreenChartIndex === index ? null : index;

    // re-render chart after resizing container
    setTimeout(() => this.renderCharts(), 200);
  }
  resetZoom(index: number) {
    const host = this.chartHosts.toArray()[index]?.nativeElement;
    const chart = this.chartInstances.get(host);

    if (chart) {
      chart.dispatchAction({
        type: 'dataZoom',
        start: 0,
        end: 100
      });
    }
  }

  downloadChart(index: number) {
    const host = this.chartHosts.toArray()[index]?.nativeElement;
    const chart = this.chartInstances.get(host);

    if (!chart) return;

    const url = chart.getDataURL({
      type: 'png',
      pixelRatio: 2
    });

    const a = document.createElement('a');
    a.href = url;
    a.download = `chart-${index + 1}.png`;
    a.click();
  }


  private toHeader(field: string): string {
    return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
